import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState, useEffect } from 'react';
import { debounce, noop } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types/pusher';
import { TableList, Page, Button, PageEmptyState, PageErrorState, PageContentHeader, useStateInRef, PaginationControl, Spinner, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useOnMount } from '~/hooks/useOnMount';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { InvoiceOptimisticUpdateType, useSubscribeToInvoiceChangesObservable } from '~/providers/InvoiceChangesObservableProvider';
import { BusinessChannelEventBinder } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import PayoutsEmptyStateIcon from './icons/payouts-empty-state.svg';
import styles from './PayoutsDashboard.module.scss';
import { applyFilters, getAccountingStatusFilterOptions, getAccountingStatusFilterOptionsFromPayouts, mapHeaderIdToBackendField, getTableConfig, PayoutsFilterType, payoutsTextsPath } from './PayoutsDashboard.utils';
import PayoutsDashboardRow from './PayoutsDashboardRow/PayoutsDashboardRow';
import img1 from './SeeWhatItsLikeImages/1.png';
import img2 from './SeeWhatItsLikeImages/2.png';
const title = 'Payouts';
export function PayoutsDashboard(props) {
    const { data, isRefreshing, onFilterChange, onSortChange, urlState: externalUrlState, sort: externalSort, pageNumber, paginationPageSize, onPageNumberChange } = props;
    const { api, logBI } = useStoreActions();
    const [sort, setSort] = useState({
        colIndex: 1,
        sort: 'desc'
    });
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11401);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { downloadCsv } = useIoContext();
    const { exportCsvFilename } = useAppConfig();
    const { showErrorToast } = useOverlayContext();
    const { t: payoutsTexts } = useLocaleKeys(payoutsTextsPath);
    const [payouts, setPayouts] = useState(data.payouts);
    useEffect(()=>{
        setPayouts(data.payouts);
    }, [
        data.payouts
    ]);
    const getPayouts = useStateInRef(payouts);
    const { urlState: internalUrlState, changeUrlState } = useStateFromUrl({
        qbo: {
            defaultValue: AccountingStatusFilter.All
        },
        xero: {
            defaultValue: AccountingStatusFilter.All
        },
        search: {
            defaultValue: ''
        },
        selected: {
            defaultValue: ''
        },
        payoutId: {
            defaultValue: ''
        }
    });
    const urlState = externalUrlState || internalUrlState;
    const { search, qbo: qboStatusFilter, xero: xeroStatusFilter, selected } = urlState;
    const currentSort = externalSort || sort;
    useOnMount(()=>{
        if (selected) {
            const element = document.querySelector('[data-id="'.concat(selected, '"]'));
            element && requestAnimationFrame(()=>element.scrollIntoView({
                    block: 'center'
                }));
        }
    });
    useSubscribeToInvoiceChangesObservable(async (param)=>{
        let { optimisticUpdate } = param;
        if (optimisticUpdate && (optimisticUpdate.type === InvoiceOptimisticUpdateType.QboIntegrationChanged || optimisticUpdate.type === InvoiceOptimisticUpdateType.XeroIntegrationChanged)) {
            const { invoiceId } = optimisticUpdate;
            const payoutToUpdate = getPayouts().find((p)=>p.invoices().some((i)=>i.id === invoiceId));
            if (!payoutToUpdate) return;
            const updatedPayout = await api.payout.getById(payoutToUpdate.id());
            setPayouts((prev)=>prev.map((prevPayout)=>prevPayout.id() === updatedPayout.id() ? updatedPayout : prevPayout));
        }
    });
    const updatePayoutFromPusher = (payload)=>{
        var _scrollElRef_current;
        const currentScrollTop = ((_scrollElRef_current = scrollElRef.current) === null || _scrollElRef_current === void 0 ? void 0 : _scrollElRef_current.scrollTop) || 0;
        setPayouts((prev)=>prev.map((prevPayout)=>{
                if (prevPayout.id() === payload.payoutId) {
                    return prevPayout.setIntegrations(payload.integrations);
                }
                return prevPayout;
            }));
        setTimeout(()=>{
            if (scrollElRef.current) {
                scrollElRef.current.scrollTop = currentScrollTop;
            }
        }, 0);
    };
    const qboStatusFilterOptions = useMemo(()=>{
        if (data.qboSyncStatusFilterCounters) {
            return getAccountingStatusFilterOptions({
                counters: data.qboSyncStatusFilterCounters,
                texts: payoutsTexts.accountingStatusLabels
            });
        }
        return getAccountingStatusFilterOptionsFromPayouts({
            payouts: applyFilters({
                payouts,
                qboStatusFilter: null,
                xeroStatusFilter: null,
                search
            }),
            checkSynced: (payout)=>payout.isQboSynced(),
            texts: payoutsTexts.accountingStatusLabels
        });
    }, [
        data.qboSyncStatusFilterCounters,
        payouts,
        search
    ]);
    const xeroStatusFilterOptions = useMemo(()=>{
        if (data.xeroSyncStatusFilterCounters) {
            return getAccountingStatusFilterOptions({
                counters: data.xeroSyncStatusFilterCounters,
                texts: payoutsTexts.accountingStatusLabels
            });
        }
        return getAccountingStatusFilterOptionsFromPayouts({
            payouts: applyFilters({
                payouts,
                qboStatusFilter: null,
                xeroStatusFilter: null,
                search
            }),
            checkSynced: (payout)=>payout.isXeroSynced(),
            texts: payoutsTexts.accountingStatusLabels
        });
    }, [
        data.xeroSyncStatusFilterCounters,
        payouts,
        search
    ]);
    const { headerItems, columnDefs, sortOptions } = useMemo(()=>getTableConfig({
            showQboIntegration,
            showXeroIntegration,
            texts: payoutsTexts.columnHeaders
        }), [
        showQboIntegration,
        showXeroIntegration
    ]);
    const filterConfig = useMemo(()=>{
        const config = {};
        if (showQboIntegration && qboStatusFilterOptions.length > 0) {
            config[PayoutsFilterType.QboStatus] = {
                type: SearchFilterType.RadioFilter,
                options: qboStatusFilterOptions.map((opt)=>({
                        value: opt.value,
                        label: opt.label,
                        sublabel: "(".concat(opt.counter, ")")
                    })),
                removable: false,
                defaultOption: AccountingStatusFilter.All
            };
        }
        if (showXeroIntegration && xeroStatusFilterOptions.length > 0) {
            config[PayoutsFilterType.XeroStatus] = {
                type: SearchFilterType.RadioFilter,
                options: xeroStatusFilterOptions.map((opt)=>({
                        value: opt.value,
                        label: opt.label,
                        sublabel: "(".concat(opt.counter, ")")
                    })),
                removable: false,
                defaultOption: AccountingStatusFilter.All
            };
        }
        return config;
    }, [
        showQboIntegration,
        showXeroIntegration,
        qboStatusFilterOptions,
        xeroStatusFilterOptions
    ]);
    const filterLabels = useMemo(()=>{
        const labels = {};
        if (showQboIntegration) {
            labels[PayoutsFilterType.QboStatus] = 'QuickBooks Online sync status';
        }
        if (showXeroIntegration) {
            labels[PayoutsFilterType.XeroStatus] = 'Xero sync status';
        }
        return labels;
    }, [
        showQboIntegration,
        showXeroIntegration
    ]);
    const tableSelectedFilters = useMemo(()=>{
        const next = {};
        if (filterConfig[PayoutsFilterType.QboStatus]) next[PayoutsFilterType.QboStatus] = qboStatusFilter;
        if (filterConfig[PayoutsFilterType.XeroStatus]) next[PayoutsFilterType.XeroStatus] = xeroStatusFilter;
        return next;
    }, [
        filterConfig,
        qboStatusFilter,
        xeroStatusFilter
    ]);
    const sortedAndFiltered = useMemo(()=>{
        return payouts;
    }, [
        payouts
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedAndFiltered,
        scrollElRef,
        alwaysRenderIndex: selected ? (data)=>data.findIndex((p)=>p.id() === selected) : undefined
    });
    function handleSortChange(sort) {
        logBI(11400, {
            column: sort ? headerItems[sort.colIndex].value : 'none'
        });
        if (onSortChange) {
            onSortChange(sort);
        } else {
            setSort(sort);
        }
    }
    function onClearSearch() {
        if (onFilterChange) {
            onFilterChange({
                search: ''
            });
        } else {
            changeUrlState({
                search: ''
            });
        }
        debouncedLogSearchToBI.onClear();
    }
    async function exportToCsv() {
        var _headerItems_currentSort_colIndex;
        const sortField = currentSort ? (_headerItems_currentSort_colIndex = headerItems[currentSort.colIndex]) === null || _headerItems_currentSort_colIndex === void 0 ? void 0 : _headerItems_currentSort_colIndex.id : undefined;
        const sortType = currentSort === null || currentSort === void 0 ? void 0 : currentSort.sort;
        const result = await handlePromise(api.payout.exportPayoutsToCsv({
            qboIntegration: qboStatusFilter,
            xeroIntegration: xeroStatusFilter,
            sort: sortField && sortType ? {
                field: mapHeaderIdToBackendField(sortField),
                type: sortType
            } : undefined,
            search: search
        }));
        if (result.success) {
            const filename = exportCsvFilename || 'exported-payouts.csv';
            downloadCsv(result.data, filename);
        } else {
            showErrorToast('Error exporting payouts to CSV', 'export-payouts-error');
        }
    }
    const hasPayoutsOrFiltersApplied = payouts.length > 0 || search || qboStatusFilter !== AccountingStatusFilter.All || xeroStatusFilter !== AccountingStatusFilter.All;
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            header: title,
            loading: isRefreshing,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: "payments-empty",
                illustration: /*#__PURE__*/ _jsx(PayoutsEmptyStateIcon, {}),
                title: "Automatic payments, with confidence",
                message: "Clients won't get any surprises at the end of the month. No manual collection or payment is needed — everything is managed by Anchor, automatically.",
                actions: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            steps: [
                                {
                                    title: 'Cashflow - flows!',
                                    subtitle: 'During the proposal approval stage, your clients can choose how they’d like to pay.',
                                    image: {
                                        src: img1,
                                        width: 1198,
                                        height: 700
                                    }
                                },
                                {
                                    title: 'Get paid on time',
                                    subtitle: 'Anchor will send reminders, handle payment processing, and ensure that late payments are a thing of the past!',
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ],
                            testId: "payouts-dashboard"
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            hidePrefix: true,
                            testId: "payments-empty-new-btn"
                        }, "create-proposal-button")
                    ]
                })
            })
        });
    const renderPayoutsPage = ()=>/*#__PURE__*/ _jsxs(Page, {
            testId: "payments-dashboard",
            ref: scrollElRef,
            header: title,
            actions: /*#__PURE__*/ _jsx(Button, {
                testId: "payments-export-csv",
                onClick: exportToCsv,
                children: "Export to CSV"
            }),
            subheader: "Review your paid invoices",
            onScroll: onScroll,
            children: [
                /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                    source: "scout",
                    eventName: PusherEventName.PayoutXeroIntegrationStatusUpdated,
                    callback: updatePayoutFromPusher
                }),
                /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                    source: "scout",
                    eventName: PusherEventName.PayoutQboIntegrationStatusUpdated,
                    callback: updatePayoutFromPusher
                }),
                /*#__PURE__*/ _jsxs(TableList.Provider, {
                    columnDefs: columnDefs,
                    expandableRows: true,
                    initialExpandedRows: selected ? {
                        [selected]: true
                    } : {},
                    singleExpandOnly: true,
                    sort: currentSort,
                    onSortChange: handleSortChange,
                    children: [
                        /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    style: {
                                        marginBottom: 24
                                    },
                                    children: /*#__PURE__*/ _jsx(TableFilters, {
                                        testIdPrefix: "payouts",
                                        search: {
                                            value: search,
                                            testId: 'payments-dashboard-search',
                                            registerHotkey: true,
                                            onChange: (event)=>{
                                                const searchStr = event.target.value;
                                                if (onFilterChange) {
                                                    onFilterChange({
                                                        search: searchStr
                                                    }, {
                                                        withDebounce: true
                                                    });
                                                } else {
                                                    changeUrlState({
                                                        search: searchStr
                                                    });
                                                }
                                                debouncedLogSearchToBI.onChange(searchStr);
                                            },
                                            onClear: onClearSearch
                                        },
                                        filterLabels: filterLabels,
                                        filterConfig: filterConfig,
                                        selectedFilters: tableSelectedFilters,
                                        setSelectedFilters: noop,
                                        mobile: {
                                            sortOptions
                                        },
                                        onRefresh: (filters)=>{
                                            var _filters_PayoutsFilterType_QboStatus, _filters_PayoutsFilterType_XeroStatus;
                                            const newQbo = (_filters_PayoutsFilterType_QboStatus = filters[PayoutsFilterType.QboStatus]) !== null && _filters_PayoutsFilterType_QboStatus !== void 0 ? _filters_PayoutsFilterType_QboStatus : AccountingStatusFilter.All;
                                            const newXero = (_filters_PayoutsFilterType_XeroStatus = filters[PayoutsFilterType.XeroStatus]) !== null && _filters_PayoutsFilterType_XeroStatus !== void 0 ? _filters_PayoutsFilterType_XeroStatus : AccountingStatusFilter.All;
                                            const filtersChanged = newQbo !== qboStatusFilter || newXero !== xeroStatusFilter;
                                            if (!filtersChanged) return;
                                            if (onFilterChange) {
                                                onFilterChange({
                                                    qbo: newQbo,
                                                    xero: newXero
                                                });
                                            } else {
                                                changeUrlState({
                                                    qbo: newQbo,
                                                    xero: newXero
                                                });
                                            }
                                        }
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: headerItems,
                                    testId: "payments-dashboard-table-header"
                                })
                            ]
                        }),
                        isRefreshing ? /*#__PURE__*/ _jsx(Spinner, {
                            centeredInBox: true,
                            centerBoxHeight: 200
                        }) : sortedAndFiltered.length ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                paginatedData.map((payout, index)=>/*#__PURE__*/ _jsx(PayoutsDashboardRow, {
                                        payout: payout,
                                        index: index,
                                        invoiceHeaders: data.invoiceHeaders
                                    }, payout.id())),
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.paginationControl,
                                    children: /*#__PURE__*/ _jsx(PaginationControl, {
                                        pageSize: paginationPageSize || 100,
                                        pageNumber: pageNumber || 1,
                                        total: data.total,
                                        onPageNumberChange: onPageNumberChange || (()=>{})
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(PayoutsEmptyStateIcon, {}),
                            title: "No payouts match your request",
                            message: "Try adjusting your search",
                            actions: /*#__PURE__*/ _jsx(Button, {
                                testId: "payments-dashboard-empty-search-clear-btn",
                                onClick: onClearSearch,
                                children: "Clear search"
                            })
                        })
                    ]
                })
            ]
        });
    return hasPayoutsOrFiltersApplied ? renderPayoutsPage() : renderEmptyPage();
}
const PAYOUTS_PAGE_SIZE = 50;
function ScoutPayoutsDashboard() {
    const { inputDebounceTime } = useAppConfig();
    const { getState } = useStoreStatic();
    const { api, fetchIntegrationsSummary } = useStoreActions();
    const [pageNumber, _setPageNumber] = useState(1);
    const { t: payoutsTexts } = useLocaleKeys(payoutsTextsPath);
    const { headerItems } = useMemo(()=>getTableConfig({
            showQboIntegration: selectors.integration.showQboIntegration(getState()),
            showXeroIntegration: selectors.integration.showXeroIntegration(getState()),
            texts: payoutsTexts.columnHeaders
        }), [
        getState
    ]);
    const sortIds = headerItems.map((item)=>{
        var _item_id;
        return (_item_id = item.id) !== null && _item_id !== void 0 ? _item_id : '';
    });
    const defaultSort = useMemo(()=>({
            colIndex: 1,
            sort: 'desc'
        }), []);
    const { sort, changeSort: _changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { urlState, changeUrlState: _changeUrlState } = useStateFromUrl({
        qbo: {
            defaultValue: AccountingStatusFilter.All
        },
        xero: {
            defaultValue: AccountingStatusFilter.All
        },
        search: {
            defaultValue: ''
        },
        selected: {
            defaultValue: ''
        },
        payoutId: {
            defaultValue: ''
        }
    });
    const urlStateRef = useRef(urlState);
    const sortRef = useRef(sort);
    const pageNumberRef = useRef(pageNumber);
    const { renderRestfulState, refresh } = useRestfulWrapper({
        fetch: ()=>{
            var _headerItems_sortRef_current_colIndex, _sortRef_current;
            const showQboIntegration = selectors.integration.showQboIntegration(getState());
            const showXeroIntegration = selectors.integration.showXeroIntegration(getState());
            if (showQboIntegration || showXeroIntegration) {
                fetchIntegrationsSummary();
            }
            const sortField = sortRef.current ? (_headerItems_sortRef_current_colIndex = headerItems[sortRef.current.colIndex]) === null || _headerItems_sortRef_current_colIndex === void 0 ? void 0 : _headerItems_sortRef_current_colIndex.id : undefined;
            const sortType = (_sortRef_current = sortRef.current) === null || _sortRef_current === void 0 ? void 0 : _sortRef_current.sort;
            return api.payout.getAllFromScout({
                qboIntegration: urlStateRef.current.qbo !== AccountingStatusFilter.All ? urlStateRef.current.qbo : undefined,
                xeroIntegration: urlStateRef.current.xero !== AccountingStatusFilter.All ? urlStateRef.current.xero : undefined,
                search: urlStateRef.current.search || undefined,
                pageSize: PAYOUTS_PAGE_SIZE,
                pageNumber: pageNumberRef.current,
                sort: sortField && sortType ? {
                    field: mapHeaderIdToBackendField(sortField),
                    type: sortType
                } : undefined
            });
        }
    });
    useOnMount(()=>{
        return ()=>refreshWithDebounce.cancel();
    });
    const refreshWithDebounce = useMemo(()=>{
        const debounceTime = inputDebounceTime != null ? inputDebounceTime : 500;
        return debounce(refresh, debounceTime);
    }, [
        refresh,
        inputDebounceTime
    ]);
    function changeUrlState(newUrlState) {
        _changeUrlState(newUrlState);
        urlStateRef.current = _object_spread({}, urlStateRef.current, newUrlState);
    }
    function changeSort(newSort) {
        _changeSort(newSort);
        sortRef.current = newSort;
    }
    function setPageNumber(pageNumber) {
        _setPageNumber(pageNumber);
        pageNumberRef.current = pageNumber;
    }
    function handleFilterChange(newUrlState, opts) {
        changeUrlState(newUrlState);
        setPageNumber(1);
        scheduleRefresh(opts);
    }
    function handleSortChange(sort) {
        changeSort(sort);
        setPageNumber(1);
        scheduleRefresh();
    }
    function handlePageNumberChange(pageNumber) {
        setPageNumber(pageNumber);
        scheduleRefresh();
    }
    function scheduleRefresh() {
        let { withDebounce } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        refreshWithDebounce();
        !withDebounce && refreshWithDebounce.flush();
    }
    return renderRestfulState({
        renderLoader: ()=>/*#__PURE__*/ _jsx(PageLoadingState, {
                header: title,
                loading: true
            }),
        renderError: (param)=>{
            let { refresh, error } = param;
            return /*#__PURE__*/ _jsx(PageErrorState, {
                title: title,
                error: error,
                onRefresh: ()=>refresh()
            });
        },
        renderChild: (param)=>{
            let { data, loading } = param;
            return /*#__PURE__*/ _jsx(PayoutsDashboard, {
                data: data,
                isRefreshing: loading,
                onFilterChange: handleFilterChange,
                onSortChange: handleSortChange,
                onPageNumberChange: handlePageNumberChange,
                onResetFilters: ()=>handleFilterChange({
                        qbo: AccountingStatusFilter.All,
                        xero: AccountingStatusFilter.All,
                        search: ''
                    }),
                urlState: urlState,
                sort: sort,
                pageNumber: pageNumber,
                paginationPageSize: PAYOUTS_PAGE_SIZE
            });
        }
    });
}
export default function WrappedPayoutsDashboard() {
    useSetDocumentTitle(title);
    return /*#__PURE__*/ _jsx(ScoutPayoutsDashboard, {});
}
